import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { events as evtApi } from '../services/api';
import { Ticket, Calendar, MapPin, ArrowLeft, Loader2, CheckCircle2, ShieldCheck } from 'lucide-react';

export default function TicketDetail() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    evtApi.ticket(ticketId).then(setTicket).catch(e => setErr(e?.response?.data?.detail || 'Ticket not found'));
  }, [ticketId]);

  if (err) return (
    <div className="max-w-lg mx-auto pbb-card p-10 text-center" data-testid="ticket-error">
      <div className="font-semibold text-white">{err}</div>
      <Link to="/my/tickets" className="text-green-400 text-sm mt-3 inline-flex items-center gap-1"><ArrowLeft size={12}/> My tickets</Link>
    </div>
  );
  if (!ticket) return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading ticket…</div>;

  const ev = ticket.event || {};
  const paid = ticket.purchaseType === 'paid';
  const checked = !!ticket.checkedInAt;

  return (
    <div className="max-w-md mx-auto space-y-4" data-testid="ticket-detail-page">
      <Link to="/my/tickets" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white"><ArrowLeft size={14}/> My tickets</Link>

      <div className="rounded-2xl overflow-hidden border-2 border-green-500/40 bg-gradient-to-b from-[#0d1f14] to-black shadow-2xl">
        <div className="p-5 border-b border-dashed border-[#1f3d27] text-center">
          <div className="text-[10px] tracking-widest text-green-400 uppercase mb-1">Ticket</div>
          <div className="text-lg font-bold text-white leading-tight" data-testid="ticket-event-title">{ev.title || ticket.eventTitle}</div>
          <div className="text-xs text-gray-300 mt-1 flex items-center justify-center gap-3 flex-wrap">
            {ev.date && <span className="inline-flex items-center gap-1"><Calendar size={11}/> {ev.date}{ev.time ? ` · ${ev.time}` : ''}</span>}
            {(ev.location || ev.city) && <span className="inline-flex items-center gap-1"><MapPin size={11}/> {[ev.location, ev.city].filter(Boolean).join(', ')}</span>}
          </div>
        </div>
        <div className="p-6 flex flex-col items-center bg-white">
          <img src={evtApi.qrUrl(ticket.ticketId)} alt="Ticket QR"
            data-testid="ticket-qr-img"
            className="w-56 h-56 rounded-xl border border-gray-200"/>
          <div className="text-xs text-gray-600 mt-3">Show this QR at the door</div>
          <div className="text-[10px] tracking-widest text-gray-500 uppercase mt-4">Ticket ID</div>
          <div className="font-mono font-bold text-black tracking-widest text-lg" data-testid="ticket-id-display">#{ticket.ticketId}</div>
          {ticket.attendeeName && ticket.attendeeName.trim().toLowerCase() !== (ticket.userName || '').trim().toLowerCase() && (
            <div className="mt-3 text-center">
              <div className="text-[10px] tracking-widest text-gray-500 uppercase">Attendee</div>
              <div className="text-black font-semibold" data-testid="ticket-attendee-name">{ticket.attendeeName}</div>
            </div>
          )}
        </div>
        <div className="p-4 flex items-center justify-between text-xs bg-[#0a0a0a]">
          <div className={`inline-flex items-center gap-1 font-semibold ${checked ? 'text-green-400' : 'text-blue-300'}`}>
            {checked ? <><CheckCircle2 size={12}/> Checked in {new Date(ticket.checkedInAt).toLocaleString()}</> : <><Ticket size={12}/> Ready — not checked in yet</>}
          </div>
          <div className={`px-2 py-0.5 rounded ${paid ? 'bg-yellow-500/20 text-yellow-300' : 'bg-gray-500/20 text-gray-300'}`}>
            {paid ? `PAID · $${(ticket.priceUsd || 0).toFixed(2)}` : 'FREE'}
          </div>
        </div>
      </div>

      {ticket.canCheckIn && !checked && (
        <div className="pbb-card p-4 flex items-start gap-2 text-xs text-gray-300">
          <ShieldCheck size={14} className="text-green-400 shrink-0 mt-0.5"/>
          <div>
            You're the host of this event — go to <Link to={`/events/${ev.id}/host`} className="underline text-green-300">the attendee dashboard</Link> to check attendees in.
          </div>
        </div>
      )}
    </div>
  );
}
